/**
 * Global Swym wishlist helpers
 * Doc reference:
 * https://developers.getswym.com/reference/build-a-custom-wishlist-button-1
 */

const SWYM_WISHLISTS_REFRESHED_EVENT = 'swym:wishlists:refreshed';
const SWYM_WISHLISTS_READY = 'swym:wishlists:ready';

window.swymcsWishlistFunctions = {
  /** @type {any | null} */
  swat: null,

  /**
   * @param {any} swat
   */
  init: function (swat) {
    this.swat = swat;
    this.fetchLists();
  },

  async addToWishlist(product, onSuccess, onError) {
    await this.renderPopup(product);

    this.fetchLists();
  },

  /**
   * @param {HTMLElement} button
   * @param {boolean} isActive
   */
  updateButtonState: function (button, isActive) {
    if (!button) return;

    const filled = button.querySelector('[data-swym-filled]');
    const unfilled = button.querySelector('[data-swym-unfilled]');

    button.setAttribute('aria-pressed', String(isActive));

    if (filled instanceof HTMLElement) {
      filled.hidden = !isActive;
    }

    if (unfilled instanceof HTMLElement) {
      unfilled.hidden = isActive;
    }

    button.classList.toggle('is-active', isActive);
  },

  /**
   * @param {{ empi: string, epi: string }} product
   * @returns {boolean}
   */
  _checkIsInWishlist: function ({ empi, epi }) {
    return (
      window.swymWishLists?.some((list) =>
        list?.listcontents?.some(
          (item) => String(item?.empi) === empi || String(item?.epi) === epi
        )
      ) ?? false
    );
  },

  /**
   * @param {HTMLElement=} button
   * @returns {boolean | null}
   */
  isProductInWishlist: function (button) {
    const epi = button?.dataset?.epi;
    const empi = button?.dataset?.empi;

    if (!epi || !empi) return false;

    const runCheck = () => {
      const isActive = !!this._checkIsInWishlist({ empi, epi });
      if (button) this.updateButtonState(button, isActive);
      return isActive;
    };

    if (window.swymWishLists) {
      return runCheck();
    }

    const onReady = () => {
      window.removeEventListener(SWYM_WISHLISTS_READY, onReady);
      runCheck();
    };

    window.addEventListener(SWYM_WISHLISTS_READY, onReady);

    return null;
  },

  /**
   * @param {{
   *   epi: string | number,
   *   du: string,
   *   cprops?: string | number
   * }} product
   */
  async renderPopup({ epi, du, cprops }) {
    /**
     * @typedef {HTMLElement & {
     *   _show?: () => void,
     *   _hide?: () => void
     * }} SwymWishlistPopupElement
     */

    /** @type {SwymWishlistPopupElement | null} */
    const popup = document.querySelector('swym-wishlist-popup');

    if (!popup) {
      console.warn('[Swym] wishlist popup element not found');
      return;
    }

    window.currentActiveProduct = await this.fetchProductsData(du);

    window.currentActiveVariant =
      window.currentActiveProduct?.variants?.find(
        /** @param {{ id: string | number }} variant */
        (variant) => String(variant.id) === String(epi)
      ) || null;

    window.currentActiveVariantId = epi;

    window.currentActiveProduct.url = du;
    window.currentActiveProduct.cprops = cprops;

    setTimeout(() => {
      popup._show?.();
    }, 100);
  },

  /**
   * @param {string} url
   * @returns {Promise<any | null>}
   */
  async fetchProductsData(url) {
    if (!url) return null;

    try {
      const endpoint = url.endsWith('.js') ? url : `${url}.js`;
      const response = await fetch(endpoint, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) return null;

      return await response.json();
    } catch (e) {
      console.warn('[Swym] Failed to fetch product data', e);
      return null;
    }
  },

  /**
   * Subscribe to wishlist readiness.
   *
   * @param {(lists: any[]) => void} callback
   */
  onWishlistsReady: function (callback) {
    if (window.swymWishLists) {
      callback(window.swymWishLists);
      return;
    }

    window.addEventListener(
      SWYM_WISHLISTS_READY,
      /** @param {CustomEvent<{ lists: any[] }>} event */
      (event) => callback(event.detail.lists),
      { once: true }
    );
  },

  async fetchLists() {
    return new Promise((resolve, reject) => {
      /** @param {SwymWishlist[]} lists */
      const onSuccess = (lists) => {
        window.swymWishLists = lists;

        this.refreshLists(lists);

        window.dispatchEvent(
          new CustomEvent(SWYM_WISHLISTS_READY, {
            detail: {
              lists,
              source: 'fetchLists',
              timestamp: Date.now(),
            },
          })
        );
        resolve(lists);
      };

      /** @param {unknown} error */
      const onError = (error) => {
        console.log('Error while fetching all Lists', error);
        reject(error);
      };

      if (!window.swymWishLists) {
        this.swat?.fetchLists({
          callbackFn: onSuccess,
          errorFn: onError,
        });
      } else {
        resolve(window.swymWishLists);
      }
    });
  },

  refreshLists: async function (lists) {
    if (!lists) {
      await this.fetchLists();
    } else {
      window.swymWishLists = lists;
    }

    this.syncWishlistButtons();
    this.updateWishlistCount();

    window.dispatchEvent(
      new CustomEvent(SWYM_WISHLISTS_REFRESHED_EVENT, {
        detail: {
          lists: window.swymWishLists,
          source: 'refreshLists',
          timestamp: Date.now(),
        },
      })
    );
  },

  syncWishlistButtons() {
    document.querySelectorAll('[data-swym-wishlist]').forEach((btn) => {
      if (!(btn instanceof HTMLElement)) return;
      this.isProductInWishlist(btn);
    });
  },

  updateWishlistCount() {
    const lists = window.swymWishLists;
    const counters = document.querySelectorAll('.wishlist-count');

    if (!Array.isArray(lists) || !counters.length) return;

    const count = lists.reduce(
      (sum, list) => sum + (list?.listcontents?.length || 0),
      0
    );

    counters.forEach((counter) => {
      counter.textContent = count > 0 ? String(count) : '';
      counter.toggleAttribute('hidden', count === 0);
    });
  },
};

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(function (swat) {
  window.swymcsWishlistFunctions.init(swat);
});
